import { Formik } from 'formik';
import { GraphQLError } from 'graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import * as yup from 'yup';

import * as graphql from '@treams/treams-network';
import {
	ArrowRightIcon,
	Button,
	Callout,
	GraphQLErrorMessage,
	Input,
	Label,
} from '@treams/treams-ui';

import PublicForm from '../../../components/authentication/PublicForm';

export default function NewPasswordRequiredChallenge(props: {
	login: string;
	context: string;
	challenge: graphql.NewPasswordRequiredChallenge;
	onResponse: (challenge: graphql.UpdateSessionResponse) => void;
	isPrefill?: boolean;
}) {
	const { t } = useTranslation('', { keyPrefix: 'common' });
	const { login, context, challenge } = props;

	const passwordSchema = yup.object({
		name: yup.string(),
		password: yup
			.string()
			.required(t('passwordValidation.required'))
			.min(14, t('passwordValidation.minLength', { count: 14 }))
			.matches(/^(?=.*[A-Z])/, t('passwordValidation.upperCase', { count: 1 }))
			.matches(/^(?=.*[a-z])/, t('passwordValidation.lowerCase', { count: 1 }))
			.matches(/^(?=.*[0-9])/, t('passwordValidation.numbers', { count: 1 }))
			.matches(
				// eslint-disable-next-line no-useless-escape
				/[-._!"`'#%&,:;<>=@{}~\$\(\)\*\+\/\\\?\[\]\^\|]+/,
				t('passwordValidation.specialCharacters', { count: 1 }),
			),
	});

	const [error, setError] = useState<GraphQLError | undefined>(undefined);

	const [commit] = graphql.useNewPasswordRequiredChallengeMutation();

	return (
		<Formik
			initialValues={{
				login,
				password: '',
			}}
			validationSchema={passwordSchema}
			onSubmit={async ({ password }) => {
				try {
					const { data } = await commit({
						variables: {
							input: {
								response: {
									context,
									newPasswordRequired: {
										username: challenge.username,
										password,
									},
								},
							},
						},
					});
					props.onResponse(data!.updateSession);
				} catch (errors) {
					setError(errors.graphQLErrors[0]);
				}
			}}
		>
			{({ errors }) => (
				<PublicForm>
					<GraphQLErrorMessage error={error} />

					{props.isPrefill ? null : (
						<Box mb={3}>
							<Label mb={2}>{t('userNameOrEmail')}:</Label>
							<Input.Formik name="login" disabled />
						</Box>
					)}
					<Box mb={3}>
						<Label mb={2}>{t('newPassword')}</Label>
						<Input.Formik name="password" type="password" autoFocus />

						{errors.password && (
							<Callout mt={2} color="red">
								{errors.password}
							</Callout>
						)}
					</Box>
					<Button.Formik
						title={t('next')}
						rightIcon={<ArrowRightIcon />}
						disabled={Boolean(errors.password)}
					/>
				</PublicForm>
			)}
		</Formik>
	);
}
